import { Cpu } from 'lucide-react'
import { useKioskGpuSummary, useKioskNodes, type KioskGpu } from '@/lib/kiosk'
import { Bar, Chip, Panel, Ring } from '@/components/kiosk/kioskUi'
import { C, fmt, tempColor } from '@/components/kiosk/kioskTheme'

function shortGpu(name: string | null, node: string): string {
  if (!name) return node
  return name
    .replace(/NVIDIA\s+/i, '')
    .replace(/GeForce\s+/i, '')
    .replace(/\s+Laptop.*/i, '')
    .trim()
}

// The tile is a small fixed-height panel, so it can only fit so many rows.
// Anything beyond this is surfaced as an explicit "+N" line rather than being
// silently dropped — a GPU missing from the list with no hint is how a newly
// added node looks broken. The fullscreen detail view has no such cap.
const MAX_TILE_ROWS = 5

/** Display order: GB10 first, then the RTX cards, then Apple, then anything
 *  else. Node order is kept within a family so the two 4090s stay adjacent. */
function gpuFamilyRank(name: string | null): number {
  const n = (name ?? '').toLowerCase()
  if (n.includes('gb10')) return 0
  if (n.includes('rtx') || n.includes('geforce')) return 1
  if (n.includes('apple')) return 2
  return 3
}

function useGpus(): (KioskGpu & { node: string; unified: boolean })[] {
  const { data: nodes } = useKioskNodes()
  return (nodes ?? []).flatMap((n) =>
    (n.gpus ?? []).map((g) => {
      // GB10 / Grace (unified LPDDR5x) returns "[N/A]" for GPU memory via
      // nvidia-smi, so mem_used/mem_total arrive null. Such a GPU shares the
      // node's system RAM, so fall back to the node's unified memory — the
      // same figure the classic web UI surfaces — instead of drawing an empty
      // 0 % bar. Guard on a single GPU per node to avoid double-counting.
      const unified = g.mem_total == null && (n.gpus?.length ?? 0) === 1
      const nm = n.latest_metrics
      return {
        ...g,
        node: n.name,
        unified,
        mem_used: unified ? g.mem_used ?? nm?.mem_used ?? null : g.mem_used,
        mem_total: unified ? g.mem_total ?? nm?.mem_total ?? null : g.mem_total,
      }
    }),
  ).sort(
    (a, b) =>
      gpuFamilyRank(a.gpu_name) - gpuFamilyRank(b.gpu_name) ||
      a.node.localeCompare(b.node) ||
      a.gpu_index - b.gpu_index,
  )
}

export function GpuTile({ onTap }: { onTap: () => void }) {
  const { data: sum } = useKioskGpuSummary()
  const gpus = useGpus()
  return (
    <Panel
      title="GPU"
      icon={<Cpu className="h-3.5 w-3.5" />}
      accent={C.violet}
      onTap={onTap}
      right={<Chip color={C.violet}>{sum?.active_gpus ?? 0}/{sum?.total_gpus ?? 0} actifs</Chip>}
    >
      <div className="flex h-full flex-col justify-between gap-1.5">
        {gpus.slice(0, MAX_TILE_ROWS).map((g) => {
          const util = g.util_pct ?? 0
          return (
            <div key={`${g.node}-${g.gpu_index}`} className="flex items-center gap-2.5">
              <Ring value={util} size={38} thickness={4} color={C.violet}>
                <span className="kmono text-[10px] font-bold" style={{ color: C.ink }}>{Math.round(util)}</span>
              </Ring>
              <div className="min-w-0 flex-1">
                <div className="flex items-baseline gap-1.5">
                  <span className="truncate text-[13px] font-semibold" style={{ color: C.ink }}>{shortGpu(g.gpu_name, g.node)}</span>
                  <span className="truncate text-[10px]" style={{ color: C.dim }}>{g.node}</span>
                </div>
                <div className="mt-1">
                  <Bar value={g.mem_used ?? 0} max={g.mem_total ?? 1} color={C.gaugeFill} height={4} />
                </div>
              </div>
              <div className="flex flex-col items-end gap-0.5">
                <span className="kmono text-[13px] font-bold" style={{ color: tempColor(g.temp_c) }}>
                  {g.temp_c != null ? `${Math.round(g.temp_c)}°` : '—'}
                </span>
                <span className="kmono text-[10px]" style={{ color: C.dim }}>{g.power_w != null ? `${Math.round(g.power_w)}W` : '—'}</span>
              </div>
            </div>
          )
        })}
        {gpus.length > MAX_TILE_ROWS && (
          <div className="text-center text-[11px]" style={{ color: C.dim }}>
            +{gpus.length - MAX_TILE_ROWS} autre{gpus.length - MAX_TILE_ROWS > 1 ? 's' : ''} — toucher pour tout voir
          </div>
        )}
        {gpus.length === 0 && <div className="flex h-full items-center justify-center text-sm" style={{ color: C.faint }}>Aucun GPU détecté</div>}
      </div>
    </Panel>
  )
}

export function GpuDetail() {
  const { data: sum } = useKioskGpuSummary()
  const gpus = useGpus()
  return (
    <div className="flex h-full flex-col gap-3">
      <div className="flex gap-2">
        {[
          { k: 'GPU actifs', v: `${sum?.active_gpus ?? 0}/${sum?.total_gpus ?? 0}`, col: C.violet },
          { k: 'util. moyenne', v: `${fmt(sum?.avg_util_pct)}%`, col: C.cyan },
          { k: 'plus chaud', v: sum?.hottest_temp_c != null ? `${Math.round(sum.hottest_temp_c)}°C` : '—', col: tempColor(sum?.hottest_temp_c) },
          // total_mem_* are BYTES (gpu_parser converts nvidia-smi MiB -> bytes,
          // and the GB10 unified-memory fallback substitutes node RAM in bytes).
          { k: 'VRAM totale', v: `${fmt(sum?.total_mem_used != null ? sum.total_mem_used / 1024 ** 3 : null, 0)}/${fmt(sum?.total_mem_capacity != null ? sum.total_mem_capacity / 1024 ** 3 : null, 0)} Go`, col: C.ink },
        ].map((s) => (
          <div key={s.k} className="flex-1 rounded-lg px-3 py-1.5" style={{ background: C.panelHi }}>
            <div className="kmono text-xl font-bold" style={{ color: s.col }}>{s.v}</div>
            <div className="text-[10px] uppercase tracking-wide" style={{ color: C.dim }}>{s.k}</div>
          </div>
        ))}
      </div>
      <div className="grid min-h-0 flex-1 grid-cols-2 gap-3">
        {gpus.map((g) => {
          const memPct = g.mem_used != null && g.mem_total ? (g.mem_used / g.mem_total) * 100 : null
          return (
            <div key={`${g.node}-${g.gpu_index}`} className="kcard flex items-center gap-4 rounded-xl border px-4"
              style={{ background: C.panel, borderColor: C.line }}>
              <Ring value={g.util_pct ?? 0} size={72} thickness={7} color={C.violet}>
                <span className="kmono text-lg font-bold" style={{ color: C.ink }}>{Math.round(g.util_pct ?? 0)}</span>
                <span className="text-[9px]" style={{ color: C.dim }}>%</span>
              </Ring>
              <div className="min-w-0 flex-1">
                <div className="truncate text-base font-semibold" style={{ color: C.ink }}>{shortGpu(g.gpu_name, g.node)}</div>
                <div className="mb-1.5 text-xs" style={{ color: C.dim }}>{g.node}</div>
                <div className="flex items-center gap-2 text-[11px]" style={{ color: C.dim }}>
                  <span className="shrink-0">{g.unified ? 'mém. unifiée' : 'VRAM'}</span>
                  <Bar value={g.mem_used ?? 0} max={g.mem_total ?? 1} color={C.gaugeFill} height={6} />
                  <span className="kmono shrink-0" style={{ color: C.ink }}>{fmt(memPct)}%</span>
                </div>
                <div className="mt-1.5 flex gap-2">
                  <Chip color={tempColor(g.temp_c)}>{g.temp_c != null ? `${Math.round(g.temp_c)}°C` : '—'}</Chip>
                  <Chip color={C.amber}>{g.power_w != null ? `${Math.round(g.power_w)} W` : '—'}</Chip>
                </div>
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}
